/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import * as Msdyn365 from '@msdyn365-commerce/core';

import { IDlvryExportCartData } from './dlvry-export-cart.data';
import { IDlvryExportCartProps } from './dlvry-export-cart.props.autogenerated';
import { UnitOfMeasure } from '../../components/unit-of-measure';

export interface IDlvryExportCartViewProps extends IDlvryExportCartProps<IDlvryExportCartData> {
    heading: JSX.Element;
    table: JSX.Element;
}

export interface IDlvryExportCartState {
}

/**
 *
 * DlvryExportCart component
 * @extends {React.PureComponent<IDlvryExportCartProps<IDlvryExportCartData>>}
 */
class DlvryExportCart extends React.PureComponent<IDlvryExportCartProps<IDlvryExportCartData>, IDlvryExportCartState> {

    constructor(props: IDlvryExportCartProps<IDlvryExportCartData>) {
        super(props);

        this.state = {};
    }

    public render(): JSX.Element | null {
        const viewProps: IDlvryExportCartViewProps = {
            ...this.props,
            heading: <Msdyn365.Text
                className={`${this.props.id}__heading`}
                tag={this.props.config.heading.tag ?? 'h2'}
                text={this.props.config.heading.text}
            />,
            table: this._renderTable()
        };

        return this.props.renderView(viewProps);
    }

    private _formatNumber(value: number): string {
        return this.props.context.cultureFormatter.formatNumber(value, { minimumFractionDigits: 2 });
    }

    private _renderTable(): JSX.Element {
        const {
            data: {
                cart
            },
            resources
        } = this.props;

        const sharedStyles: React.CSSProperties = {
            whiteSpace: 'nowrap'
        };

        return (
            <table className={`${this.props.id}__table`}>
                <thead>
                    <tr>
                        <th align='left' style={sharedStyles}>
                            <span style={{ fontSize: 0, lineHeight: 1 }}>#</span> {/* Used to ignore line during import */}
                            {resources.itemNumberHeader}
                        </th>
                        <th align='left' style={sharedStyles}>{resources.quantityHeader}</th>
                        <th align='left' style={sharedStyles}>{resources.anbrekkHeader}</th>
                        <th align='left' style={sharedStyles}>{resources.totalQuantityHeader}</th>
                        <th align='left' style={sharedStyles}>{resources.itemNameHeader}</th>
                        <th align='left' style={sharedStyles}>{resources.brandHeader}</th>
                        <th align='left' style={sharedStyles}>{resources.stockStatusHeader}</th>
                        <th align='left' style={sharedStyles}>{resources.storageHeader}</th>
                        <th align='left' style={sharedStyles}>{resources.unitPriceHeader}</th>
                        <th align='left' style={sharedStyles}>{resources.totalPriceHeader}</th>
                        <th align='left' style={sharedStyles}>{resources.unitHeader}</th>
                    </tr>
                </thead>
                <tbody>
                    {this.props.data.productsExtended.result?.map((product) => {
                        const cartLine = cart.result?.cart.CartLines?.find(x => x.ProductId === product.RecordId);

                        return (
                            <tr key={product.RecordId}>
                                <td align='right' style={sharedStyles}>{product.ItemId}</td>
                                <td align='left' style={sharedStyles}>{this._formatNumber(cartLine?.Quantity ?? 0)}</td>
                                <td align='left' style={sharedStyles}>{this.props.resources.no}</td>
                                <td align='left' style={sharedStyles}>{this._formatNumber(cartLine?.Quantity ?? 0)}</td>
                                <td align='left' style={sharedStyles}>{product.Name}</td>
                                <td align='left' style={sharedStyles}>{product.Brand}</td>
                                <td align='left' style={sharedStyles}>{product.AvailabilityLabel}</td>
                                <td align='left' style={sharedStyles}>{product.Storage}</td>
                                <td align='left' style={sharedStyles}>{this._formatNumber(cartLine?.Price ?? 0)}</td>
                                <td align='left' style={sharedStyles}>{this._formatNumber((cartLine?.Price ?? 0) * (cartLine?.Quantity ?? 0))}</td>
                                <td align='left' style={sharedStyles}>
                                    <UnitOfMeasure context={this.props.context} simpleProduct={product} />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        );
    }
}

export default DlvryExportCart;
